import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { DashboardWorkStyleStep } from '../../../../../../assets/images'
import { useOnboardingFormContext } from '../../../../contexts/OnboardingFormContext'
import { OnboardingWorkMode } from '../../../../enums/onboardingWorkMode'
import { usePredefinedOnboardingStepValue } from '../../../../hooks/usePredefinedOnboardingStepValue'
import { useRegisterOnboardingStep } from '../../../../hooks/useRegisterOnboardingStep'
import { useSubmitOnboardingStepForm } from '../../../../hooks/useSubmitOnboardingStepForm'
import { useTrackOnboarding } from '../../../../hooks/useTrackOnboarding'
import { CompanyOnboarding } from '../../../../types/companyOnboarding'
import { OnboardingForm } from '../../../../types/onboardingForm'
import { SingleCardSelect } from '../SingleCardSelect'
import { StepContainer } from '../StepContainer'
import { getWorkStyleOptions } from './utils/getWorkStyleOptions'

const workStyleAnalyticsNames: Record<OnboardingWorkMode, string> = {
  [OnboardingWorkMode.Alone]: 'alone',
  [OnboardingWorkMode.HaveEmployees]: 'has_employees',
  [OnboardingWorkMode.WithContractors]: 'works_with_freelancers',
}

const selectWorkStyleValue = (onboarding?: CompanyOnboarding): Partial<OnboardingForm> => ({
  workStyle: onboarding?.workMode ?? undefined,
})

const buildWorkStylePayload = (values: OnboardingForm) =>
  values.workStyle ? { workMode: values.workStyle } : undefined

export const WorkStyleStep = (): ReactElement => {
  const { t } = useTranslation()
  const { onboardingForm } = useOnboardingFormContext()
  const { clearErrors, FormItem } = onboardingForm
  const { trackOnboarding } = useTrackOnboarding()

  const { isLoading: isLoadingPredefinedValues, predefinedValues } =
    usePredefinedOnboardingStepValue(selectWorkStyleValue)
  const { isSubmitting, submitForm } = useSubmitOnboardingStepForm(buildWorkStylePayload)

  const submitHandler = useCallback(
    async (values: OnboardingForm) => {
      await submitForm(values)
    },
    [submitForm],
  )

  useRegisterOnboardingStep({
    isLoading: isLoadingPredefinedValues,
    isSubmitting,
    predefinedValues,
    submitHandler,
  })

  const options = useMemo(() => getWorkStyleOptions(t), [t])

  return (
    <StepContainer image={<DashboardWorkStyleStep />} title={t('onboarding.steps.work_style.title')}>
      <FormItem
        name="workStyle"
        render={({ field: { error, onChange, value } }) => (
          <SingleCardSelect
            disabled={isLoadingPredefinedValues}
            hasError={!!error}
            onChange={(id) => {
              onChange(id)
              clearErrors('workStyle')
              trackOnboarding('Work style selected', { selectedWorkStyle: workStyleAnalyticsNames[id] })
            }}
            options={options}
            value={value}
          />
        )}
      />
    </StepContainer>
  )
}
